import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { MultiSelect, Option } from "@/components/ui/multi-select";
import { useUpdateUserRoleScope } from "@/lib/api/roles";
import { useGroups } from "@/lib/api/group";
import { useAbility } from "@/lib/casl/ability-context";

interface EditScopeDialogProps {
  userId: string;
  roleId: string;
  roleName: string;
  currentScope: { groups?: string[] };
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function EditScopeDialog({
  userId,
  roleId,
  roleName,
  currentScope,
  open,
  onOpenChange,
}: EditScopeDialogProps) {
  const { data: groups } = useGroups(false);
  const updateScope = useUpdateUserRoleScope(userId);
  const ability = useAbility();
  const canManageAll = ability.can("manage", "all");

  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);

  const groupOptions = useMemo<Option[]>(
    () =>
      groups?.map((group) => ({
        value: group.id,
        label: group.name,
      })) ?? [],
    [groups],
  );

  const selectedGroups = useMemo(
    () =>
      selectedGroupIds.map(
        (groupId) =>
          groupOptions.find((group) => group.value === groupId) ?? {
            value: groupId,
            label: groupId,
          },
      ),
    [groupOptions, selectedGroupIds],
  );

  useEffect(() => {
    if (open) {
      setSelectedGroupIds(currentScope.groups ?? []);
    }
  }, [open, currentScope]);

  const handleSubmit = () => {
    updateScope.mutate(
      { roleId, scope: { groups: selectedGroupIds } },
      {
        onSuccess: () => {
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Group Scope — {roleName}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Groups</Label>
            <p className="text-xs text-muted-foreground">
              {canManageAll
                ? "Select which groups this role is scoped to. Leave empty to remove group scoping."
                : "Select at least one group. You cannot remove all groups from scope."}
            </p>
            <MultiSelect
              options={groupOptions}
              value={selectedGroups}
              onChange={(selected) =>
                setSelectedGroupIds(selected.map((option) => option.value))
              }
              placeholder="Select groups..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              updateScope.isPending ||
              (!canManageAll && selectedGroupIds.length === 0)
            }
          >
            {updateScope.isPending ? "Saving..." : "Save Scope"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
